import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { safeHtml } from "../lib/sanitize";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterStrip from "../components/NewsletterStrip";
import AffiliateDisclosure from "../components/AffiliateDisclosure";
import { renderAffiliateBlock } from "../components/AffiliateBlocks";
import { ArticleCard, prettyCategory } from "../components/ContentCards";
import VideoBlogSection from "../components/VideoBlogSection";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [cardsById, setCardsById] = useState({});
  const [related, setRelated] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/articles/slug/${slug}`);
        setArticle(data);
        const cardIds = [...new Set((data.affiliate_embed_blocks || []).flatMap((b) => b.card_ids || []))];
        if (cardIds.length) {
          const cardsRes = await Promise.all(cardIds.map((id) => api.get(`/cards/${id}`).then((r) => r.data).catch(() => null)));
          const map = {};
          cardsRes.forEach((c) => c && (map[c.id] = c));
          setCardsById(map);
        }
        const rel = await api.get(`/articles?category=${data.category}&status=published&limit=4`);
        setRelated(rel.data.items.filter((a) => a.slug !== slug).slice(0, 3));
      } catch (err) {
        console.error("Failed to load article:", err);
      }
    })();
  }, [slug]);

  // Split body at [AFFILIATE_CARD_BLOCK] placeholders or by blocks positions
  const bodySegments = useMemo(() => {
    if (!article) return [];
    const body = article.body_rich_text || "";
    const parts = body.split("[AFFILIATE_CARD_BLOCK]");
    return parts;
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container-page py-24 font-mono text-sm text-[#4A4742]">Loading…</div>
      </div>
    );
  }

  const hasDisclosure = article.affiliate_disclosure_enabled && (article.affiliate_embed_blocks?.length > 0);
  const blocks = article.affiliate_embed_blocks || [];
  const stickyBlock = blocks.find((b) => b.block_type === "sticky_mobile_card");
  const endBlock = blocks.find((b) => b.block_type === "end_of_article_best_cards");
  const inlineBlocks = blocks.filter((b) => !["sticky_mobile_card", "end_of_article_best_cards"].includes(b.block_type));

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Header />

      <article className="container-page pt-10 md:pt-16 pb-20 max-w-4xl" data-testid="article-page">
        {/* Breadcrumbs */}
        <nav className="text-xs font-mono text-[#4A4742] mb-6" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-[#1B3B36]">Home</Link>
          <span className="mx-2">/</span>
          <Link to={`/${article.category}`} className="hover:text-[#1B3B36]">{prettyCategory(article.category)}</Link>
        </nav>

        <div className="overline text-[#B4533C]">{prettyCategory(article.category)}</div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.03] mt-3" data-testid="article-title">{article.title}</h1>
        <p className="mt-6 text-lg text-[#4A4742] leading-relaxed max-w-3xl">{article.excerpt}</p>

        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-mono text-[#4A4742]">
          <div className="flex items-center gap-2" data-testid="author-box">
            <div className="w-7 h-7 rounded-full bg-[#1B3B36] text-white grid place-items-center text-[10px]">{article.author?.[0] || "J"}</div>
            <span>By {article.author}</span>
          </div>
          {article.reviewer && (<><span>·</span><span>Reviewed by {article.reviewer}</span></>)}
          <span>·</span>
          <span>Updated {new Date(article.updated_at).toLocaleDateString()}</span>
        </div>

        {article.featured_image && (
          <div className="mt-10 aspect-[16/9] overflow-hidden bg-[#EFECE5] border border-[#D6CFC4]">
            <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        {hasDisclosure && <AffiliateDisclosure />}

        <div className="prose-editorial mt-10 max-w-3xl">
          {bodySegments.map((seg, idx) => (
            <div key={`seg-${idx}-${seg.slice(0, 20)}`}>
              <div dangerouslySetInnerHTML={safeHtml(seg)} />
              {idx < bodySegments.length - 1 && inlineBlocks[idx] && renderAffiliateBlock(inlineBlocks[idx], cardsById, article.id)}
            </div>
          ))}
          {/* if inline blocks left un-placed */}
          {bodySegments.length === 1 && inlineBlocks.map((b) => <div key={`inline-${b.block_type}-${b.position}`}>{renderAffiliateBlock(b, cardsById, article.id)}</div>)}
        </div>

        {/* FAQ */}
        {article.faq_items?.length > 0 && (
          <section className="mt-14 max-w-3xl" data-testid="faq-section">
            <div className="overline text-[#B4533C]">Frequently Asked</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-6">Common questions</h2>
            <div className="space-y-4">
              {article.faq_items.map((f, i) => (
                <details key={`faq-${i}-${f.question.slice(0, 40)}`} className="border-b border-[#D6CFC4] pb-4" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer font-serif text-lg">{f.question}</summary>
                  <p className="mt-2 text-sm text-[#4A4742] leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {endBlock && renderAffiliateBlock(endBlock, cardsById, article.id)}

        {/* Video blog module — contextual to article tags */}
        <VideoBlogSection variant="inline" tag={article.tags?.[0]} heading="Watch the trip on film" />

        {/* Newsletter inline */}
        {article.newsletter_cta_enabled && (
          <div className="my-12 max-w-3xl">
            <NewsletterStrip compact />
          </div>
        )}
      </article>

      {related.length > 0 && (
        <section className="container-page py-16 border-t border-[#D6CFC4]">
          <div className="overline text-[#B4533C]">Keep reading</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-8">Related from the desk</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}

      {stickyBlock && renderAffiliateBlock(stickyBlock, cardsById, article.id)}

      <NewsletterStrip />
      <Footer />
    </div>
  );
}

